/**
 * PresenceAvatars - Shows connected editors in document collaboration
 *
 * Features:
 * - Colored avatar circles for each connected user
 * - Tooltips showing user names
 * - Stacked display with overflow indicator
 * - Connection status indicator
 */

import { Users, Wifi, WifiOff, Loader2, Bot } from 'lucide-react';
import { getBrandColor } from '../../utils/brandColor';
import type { CollaborativeUser } from '../../contexts/CollaborativeContext';

interface PresenceAvatarsProps {
  users: CollaborativeUser[];
  isConnected: boolean;
  isConnecting: boolean;
  maxVisible?: number;
  currentUserId?: string;
}

export function PresenceAvatars({
  users,
  isConnected,
  isConnecting,
  maxVisible = 4,
  currentUserId,
}: PresenceAvatarsProps) {
  // Dedup by user ID and filter out current user
  const uniqueUsers = Array.from(
    users.reduce((map, u) => {
      if (u.id && u.id !== currentUserId) map.set(u.id, u);
      return map;
    }, new Map<string, CollaborativeUser>()).values()
  );
  const visibleUsers = uniqueUsers.slice(0, maxVisible);
  const overflowCount = uniqueUsers.length - maxVisible;

  // Get initials from name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  return (
    <div className="flex items-center gap-2">
      {/* Connection status */}
      <div className="flex items-center gap-1 text-xs">
        {isConnecting ? (
          <span className="flex items-center gap-1 text-amber-500">
            <Loader2 size={12} className="animate-spin" />
            <span className="hidden sm:inline">Connecting</span>
          </span>
        ) : isConnected ? (
          <span className="flex items-center gap-1 text-emerald-500">
            <Wifi size={12} />
            <span className="hidden sm:inline">Live</span>
          </span>
        ) : (
          <span className="flex items-center gap-1 text-neutral-400">
            <WifiOff size={12} />
            <span className="hidden sm:inline">Offline</span>
          </span>
        )}
      </div>

      {/* Separator */}
      {uniqueUsers.length > 0 && (
        <div className="h-4 w-px bg-neutral-200" />
      )}

      {/* User avatars */}
      {uniqueUsers.length > 0 && (
        <div className="flex items-center -space-x-2">
          {visibleUsers.map((user) => (
            <div
              key={user.id}
              className="relative group"
            >
              {/* Avatar — agents get a bot icon, humans get initials */}
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium
                         text-white border-2 border-white shadow-sm transition-transform hover:scale-110 hover:z-10
                         ${user.isAgent ? 'ring-2 ring-coral-300 ring-offset-1' : ''}`}
                style={{ backgroundColor: user.isAgent ? getBrandColor('coral-500') : user.color }}
                title={user.isAgent ? `${user.name} (AI)` : user.name}
              >
                {user.isAgent ? <Bot size={14} /> : getInitials(user.name)}
              </div>

              {/* Tooltip */}
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-neutral-800
                         text-white text-xs whitespace-nowrap opacity-0 invisible group-hover:opacity-100
                         group-hover:visible transition-all pointer-events-none z-50"
              >
                {user.name}{user.isAgent ? ' (AI)' : ''}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-800" />
              </div>

              {/* Cursor indicator (if user has active cursor) */}
              {user.cursor && (
                <div
                  className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full bg-white border-2"
                  style={{ borderColor: user.color }}
                />
              )}
            </div>
          ))}

          {/* Overflow indicator */}
          {overflowCount > 0 && (
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium
                       bg-neutral-200 text-neutral-600 border-2 border-white"
              title={`${overflowCount} more ${overflowCount === 1 ? 'person' : 'people'}`}
            >
              +{overflowCount}
            </div>
          )}
        </div>
      )}

      {/* Empty state - just you */}
      {uniqueUsers.length === 0 && isConnected && (
        <div className="flex items-center gap-1 text-xs text-neutral-400">
          <Users size={12} />
          <span className="hidden sm:inline">Just you</span>
        </div>
      )}
    </div>
  );
}

export default PresenceAvatars;
